import { cn } from '@rocco/utils/cn';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';

/**
 * The brand badge the detail pages hang off a banner's edge: a 164px disc, a
 * lit stroke around it, and a gap between the stroke and the mark.
 *
 * The stroke is a masked ring rather than a border colour. A border takes one
 * colour and `border-image` does not follow a radius, so neither can carry a
 * gradient round a circle. The radial mask keeps everything inside the stroke
 * transparent, which the gap has to be: the badge straddles the banner's edge,
 * so it shows the photograph above and the page below, and anything filled
 * would band across both.
 *
 * Shared because the three detail pages were drawing it three ways — a
 * gradient ring here, a flat contrast border there — and the badge is the same
 * object on all of them.
 */
const RING = {
    background:
        'linear-gradient(to bottom, #ffffff 0%, #ffffff 35%, #d7d7d7 100%)',
    WebkitMaskImage:
        'radial-gradient(farthest-side, transparent calc(100% - 0.25rem), #000 calc(100% - 0.25rem))',
    maskImage:
        'radial-gradient(farthest-side, transparent calc(100% - 0.25rem), #000 calc(100% - 0.25rem))',
} as const;

/** The disc, its lift, and the size the design draws it at. */
export const DETAIL_LOGO_BADGE = cn(
    'relative block size-[10.25rem] rounded-full',
    'shadow-[0px_4px_14px_10px_rgba(0,0,0,0.05)]',
);

export const DetailLogoBadge = ({
    src,
    alt,
    background,
    className,
}: {
    src: string;
    alt: string;
    background?: string | null;
    className?: string;
}) => (
    <span className={cn(DETAIL_LOGO_BADGE, className)}>
        <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 rounded-full"
            style={RING}
        />
        {/* Inset by the stroke plus the gap it stands off by. */}
        <ProfessionalTileLogo
            image={{ src, alt }}
            background={background ?? null}
            className="absolute inset-[0.5rem]"
        />
    </span>
);
